import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function LoginPage() {
  const { user, login, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (ready && user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background lg:flex-row">
      {/* Hero */}
      <div className="login-hero relative flex min-h-[38vh] items-end p-8 lg:min-h-screen lg:w-1/2">
        <div className="anim-fade-up relative z-10 mb-6 flex flex-col items-center gap-3 text-center text-white lg:mb-16">
          <Logo className="h-20 w-20 shadow-lg" />
          <h1 className="text-3xl font-black leading-tight text-white drop-shadow-lg lg:text-4xl">
            Investors Property Consultant
          </h1>
          <p className="max-w-md text-sm text-white/90 lg:text-base">
            Pune&apos;s Trusted Real Estate CRM
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-1 items-center justify-center p-6 lg:p-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-6 anim-fade-up"
          data-testid="login-form"
        >
          <div>
            <h2 className="text-3xl font-bold text-navy">Welcome Back</h2>
            <p className="mt-1 text-sm text-slate-500">Sign in to continue managing your leads</p>
          </div>

          <div className="space-y-3">
            <label className="relative block">
              <span className="sr-only">Email</span>
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="login-email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm outline-none ring-offset-1 focus:border-[hsl(var(--brand-navy))]"
              />
            </label>
            <label className="relative block">
              <span className="sr-only">Password</span>
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="login-password"
                required
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-10 text-sm outline-none focus:border-[hsl(var(--brand-navy))]"
              />
              <button
                type="button"
                data-testid="login-toggle-password"
                onClick={() => setShow((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </label>
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-xl bg-navy text-base font-semibold hover:brightness-110"
          >
            {loading ? "Signing in…" : "Sign In"}
          </Button>

          <div className="text-center">
            <button
              type="button"
              data-testid="login-forgot"
              onClick={() => toast("Contact your admin to reset your password", { icon: "🔐" })}
              className="text-sm font-semibold text-navy hover:underline"
            >
              Forgot password?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
